import { RouteRemap } from '@/constants/navigation';
import { AppMenuModel } from '@/models/AppViewModel';

export const normalizePath = (path: string | undefined): string => {
  const raw = (path ?? '').trim();
  if (!raw) {
    return '';
  }

  const sanitizedSource = raw.split(/[?#]/, 1)[0];

  let sanitized = '';
  let previousWasSlash = false;
  for (const char of sanitizedSource) {
    if (char === '/') {
      if (!previousWasSlash) {
        sanitized += '/';
        previousWasSlash = true;
      }
      continue;
    }
    sanitized += char;
    previousWasSlash = false;
  }

  while (sanitized.endsWith('/') && sanitized.length > 1) {
    sanitized = sanitized.slice(0, -1);
  }

  if (!sanitized || sanitized === '/') {
    return '';
  }

  if (!sanitized.startsWith('/')) {
    sanitized = `/${sanitized}`;
  }

  return sanitized.toLowerCase();
};

const humanizeSegment = (segment: string): string => {
  if (!segment) return '';
  const replaced = segment
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return replaced
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
};

export function filterAppMenu(
  menuItems: AppMenuModel[] | undefined,
  deprecatedRoutes: string[],
  remaps: RouteRemap[] = [],
): AppMenuModel[] {
  if (!menuItems) {
    return [];
  }

  const deprecatedSet = new Set(deprecatedRoutes.map(normalizePath));

  const filterRecursive = (items: AppMenuModel[]): AppMenuModel[] => {
    return items
      .map((item) => {
        const newItem: AppMenuModel = { ...item };
        if (newItem.Items) {
          newItem.Items = filterRecursive(newItem.Items);
        }
        const isDeprecated = deprecatedSet.has(normalizePath(newItem.Path));
        const children = newItem.Items;
        const hasChildren = Boolean(children && children.length > 0);

        if (isDeprecated && children && children.length > 0) {
          newItem.Path = children[0].Path;
          return newItem;
        }

        if (isDeprecated && !hasChildren) {
          return null;
        }

        return newItem;
      })
      .filter((i): i is AppMenuModel => i !== null);
  };

  let filtered = filterRecursive(menuItems.map((m) => ({ ...m })));

  const pathMap = new Map<string, AppMenuModel>();
  const parentMap = new Map<string, AppMenuModel | null>();
  const buildMaps = (items: AppMenuModel[], parent: AppMenuModel | null) => {
    for (const item of items) {
      if (item.Path) {
        const key = normalizePath(item.Path);
        pathMap.set(key, item);
        parentMap.set(key, parent);
      }
      if (item.Items) {
        buildMaps(item.Items, item);
      }
    }
  };
  buildMaps(filtered, null);

  // Recursively collect all nodes (and their parents) that match a given path key.
  const collectNodesWithPath = (
    items: AppMenuModel[],
    parent: AppMenuModel | null,
    targetKey: string,
    out: Array<{ node: AppMenuModel; parent: AppMenuModel | null }>,
  ) => {
    for (const node of items) {
      const key = normalizePath(node.Path);
      if (key === targetKey) {
        out.push({ node, parent });
      }
      if (node.Items && node.Items.length > 0) {
        collectNodesWithPath(node.Items, node, targetKey, out);
      }
    }
  };

  for (const { from, to } of remaps) {
    const fromKey = normalizePath(from);
    const toKey = normalizePath(to);
    const item = pathMap.get(fromKey);
    if (!item) continue;

    const oldParent = parentMap.get(fromKey);
    if (oldParent) {
      oldParent.Items = (oldParent.Items || []).filter(
        (c) => normalizePath(c.Path) !== fromKey,
      );
    } else {
      filtered = filtered.filter((c) => normalizePath(c.Path) !== fromKey);
    }
    pathMap.delete(fromKey);
    parentMap.delete(fromKey);

    item.Path = to;

    const toSegmentsOriginal = to.split('/').filter(Boolean);
    const leafName = humanizeSegment(
      toSegmentsOriginal[toSegmentsOriginal.length - 1] || '',
    );
    if (leafName) {
      item.Name = leafName;
    }

    // Ensure all parent segments exist as menu nodes before attaching the leaf
    const parentSegments = toSegmentsOriginal.slice(
      0,
      Math.max(0, toSegmentsOriginal.length - 1),
    );
    let currentParent: AppMenuModel | null = null;
    let currentPath = '';
    for (let i = 0; i < parentSegments.length; i++) {
      currentPath += '/' + parentSegments[i];
      const currentKey = normalizePath(currentPath);
      let node = pathMap.get(currentKey);
      if (!node) {
        node = {
          IsParent: true,
          Name: humanizeSegment(parentSegments[i]),
          Path: currentPath,
          Items: [],
        };
        if (currentParent) {
          if (!currentParent.Items) currentParent.Items = [];
          currentParent.Items.push(node);
        } else {
          filtered.push(node);
        }
        pathMap.set(currentKey, node);
        parentMap.set(currentKey, currentParent);
      }
      currentParent = node;
    }

    if (currentParent) {
      if (!currentParent.Items) currentParent.Items = [];
      currentParent.Items.push(item);
      parentMap.set(toKey, currentParent);
    } else {
      filtered.push(item);
      parentMap.set(toKey, null);
    }
    pathMap.set(toKey, item);

    // Handle edge case: a parent may also share the same `from` path.
    // After remapping the leaf, ensure no parent remains pointing to the old path.
    const leftovers: Array<{
      node: AppMenuModel;
      parent: AppMenuModel | null;
    }> = [];
    collectNodesWithPath(filtered, null, fromKey, leftovers);
    for (const { node, parent } of leftovers) {
      // Skip if this node was already moved (path changed) or is the same instance
      if (normalizePath(node.Path) !== fromKey) continue;

      const children = node.Items || [];
      // Ensure no lingering children have the deprecated path
      const remainingChildren = children.filter(
        (c) => normalizePath(c.Path) !== fromKey,
      );
      node.Items = remainingChildren;

      const hasChildren = remainingChildren.length > 0;
      if (!hasChildren) {
        // Remove this node entirely if it no longer has children
        if (parent) {
          parent.Items = (parent.Items || []).filter((c) => c !== node);
        } else {
          filtered = filtered.filter((c) => c !== node);
        }
        continue;
      }

      // If the parent still has children, make its path follow the first child's path
      node.Path = remainingChildren[0].Path;

      // Update maps to reflect the new path for this node
      const newKey = normalizePath(node.Path);
      pathMap.set(newKey, node);
      parentMap.set(newKey, parent);
    }
  }

  const syncIsParentFlag = (items: AppMenuModel[]) => {
    for (const node of items) {
      const children = node.Items;
      const childCount = children?.length ?? 0;
      node.IsParent = childCount > 0;
      if (children && children.length > 0) {
        syncIsParentFlag(children);
      }
    }
  };

  syncIsParentFlag(filtered);

  return filtered;
}

/**
 * Paths from the raw (pre-filter) backend menu that match `routes`. Lets
 * routes hidden from the sidebar still be permission-checked against what
 * the backend granted.
 */
export function collectGrantedPaths(
  menuItems: AppMenuModel[] | undefined,
  routes: readonly string[],
): string[] {
  const wanted = new Set(routes.map(normalizePath));
  const granted = new Set<string>();
  const walk = (items: AppMenuModel[] | undefined) => {
    for (const item of items ?? []) {
      const key = normalizePath(item.Path);
      if (wanted.has(key)) granted.add(key);
      walk(item.Items);
    }
  };
  walk(menuItems);
  return [...granted];
}
